import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);
  /** @type {Map<number, number>} */
  #cartItemErrorHideTimeouts = new Map();

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);

    this.#saveProgressBarHTML();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);

    for (const timeoutId of this.#cartItemErrorHideTimeouts.values()) {
      clearTimeout(timeoutId);
    }
    this.#cartItemErrorHideTimeouts.clear();
  }

  /**
   * Saves the progress bar HTML to localStorage before morphing, so it can be restored if it gets removed during morphing.
   */
  #saveProgressBarHTML() {
    const progressBar = document.querySelector('.bv-cart-progress');
    if (progressBar) {
      localStorage.setItem('bv-progress-html', progressBar.outerHTML);
    }
  }

  /**
   * Saves the HTML of protected elements (like the progress bar) before morphing, so they can be restored if they get removed during morphing. This is necessary because some themes (like Dawn) remove all non-cart-item elements from the cart drawer before morphing, which causes our progress bar to be removed as well. By saving the HTML before morphing and restoring it after, we ensure that the progress bar remains visible and functional for customers.
   */
  #preserveProtectedElements() {
    const progressBar = document.querySelector('.bv-cart-progress');
    if (progressBar) {
      this._protectedProgressBar = progressBar.outerHTML;
    }
  }

  /**
   * Returns the protected elements to the cart if they were removed during morphing and the cart is not empty. This can happen because some themes (like Dawn) remove all non-cart-item elements from the cart drawer before morphing, which causes our protected elements to be removed as well. By restoring them after morphing, we ensure that they remain visible and functional for customers.
   */
  /**
   * @param {number | null} itemCount
   */
  #restoreProtectedElements(itemCount = null) {
    if (this._protectedProgressBar) {
      const cartContent = document.querySelector('.cart-drawer__content');
      const currentProgressBar = cartContent?.querySelector('.bv-cart-progress');
      
      if (cartContent && !currentProgressBar) {
        // Only restore when cart isn't empty. We prefer the API-provided itemCount
        // (since we may not be morphing the dialog element that carries the empty class).
        const isCartEmpty = typeof itemCount === 'number' ? itemCount === 0 : !!document.querySelector('.cart-drawer--empty');
        if (!isCartEmpty) {
          cartContent.insertAdjacentHTML('afterbegin', this._protectedProgressBar);
        }
      }
    }
  }

  /**
   * Keeps the cart drawer dialog empty class in sync when we morph only a subtree.
   * @param {number} itemCount
   */
  #syncDrawerEmptyState(itemCount) {
    const dialog = this.closest('dialog');
    if (!(dialog instanceof HTMLDialogElement)) return;
    if (!dialog.classList.contains('cart-drawer__dialog')) return;

    dialog.classList.toggle('cart-drawer--empty', itemCount === 0);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.classList.add('loading');

    this.#preserveProtectedElements();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionDocument = new DOMParser().parseFromString(parsedResponseText.sections[this.sectionId], 'text/html');

         // Prefer the authoritative cart item_count from the Cart API response.
         // (Not all cart sections include a dedicated DOM node for item count.)
         const apiItemCount = Number(parsedResponseText.item_count);
         const newCartItemCount = Number.isFinite(apiItemCount)
           ? apiItemCount
           : (() => {
               const fromDom = newSectionDocument.querySelector('[ref="cartItemCount"]')?.textContent;
               const parsed = fromDom ? parseInt(fromDom, 10) : 0;
               return Number.isFinite(parsed) ? parsed : 0;
             })();

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        // Update only this cart-items-component subtree to avoid re-rendering parent containers
        // (e.g. header sections or open <dialog> drawers), which can cause layout jumps.
         const newCartItemsComponent =
           this.#findMatchingComponentInSectionHTML(newSectionDocument) ||
           newSectionDocument.querySelector('cart-items-component');

         if (newCartItemsComponent instanceof HTMLElement) {
           morph(this, newCartItemsComponent, { childrenOnly: true });
         } else {
           // Last resort: morph the whole section if we couldn't locate the component.
           // This can affect open dialogs, so we keep it as a true fallback.
           morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
         }

         // Keep empty state class (on the dialog) in sync when we only morph children.
         this.#syncDrawerEmptyState(newCartItemCount);

         setTimeout(() => {
           this.#restoreProtectedElements(newCartItemCount);
           document.dispatchEvent(new CustomEvent('cart:update-complete', { 
             detail: { 
               sectionId: this.sectionId,
               itemCount: newCartItemCount 
             } 
           }));
         }, 50);

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        setTimeout(() => {
          const newCartTotal = document.querySelector('[data-cart-subtotal]');
          newCartTotal?.classList.remove('loading');
        }, 200);

        this.#updateCartQuantitySelectorButtonStates();

        if (parsedResponseText.item_count === 0 && parsedResponseText.discount_codes?.length) {
          this.removeCouponCodes();
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  removeCouponCodes = () => {
    fetch(Theme.routes.cart_update_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        discount: ""
      })
    }).then(response => response.json())
  }

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    // Cancel the UI change: the cart didn't accept the new quantity.
    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');

    const existingTimeout = this.#cartItemErrorHideTimeouts.get(line);
    if (existingTimeout) clearTimeout(existingTimeout);

    const timeoutId = window.setTimeout(() => {
      cartItemErrorContainer.classList.add('hidden');
      cartItemError.textContent = '';
      this.#cartItemErrorHideTimeouts.delete(line);
    }, 2000);

    this.#cartItemErrorHideTimeouts.set(line, timeoutId);
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event?.detail?.data?.sections?.[this.sectionId];
    if (cartItemsHtml) {
      this.#preserveProtectedElements();
      
      const newSectionDocument = new DOMParser().parseFromString(cartItemsHtml, 'text/html');
       const newCartItemsComponent =
         this.#findMatchingComponentInSectionHTML(newSectionDocument) ||
         newSectionDocument.querySelector('cart-items-component');

       if (newCartItemsComponent instanceof HTMLElement) {
         morph(this, newCartItemsComponent, { childrenOnly: true });
       } else {
         morphSection(this.sectionId, cartItemsHtml);
       }

       // Best-effort: if the event carries an itemCount, keep the drawer empty class in sync.
       const maybeItemCount = typeof event?.detail?.data?.itemCount === 'number' ? event.detail.data.itemCount : null;
       if (typeof maybeItemCount === 'number') this.#syncDrawerEmptyState(maybeItemCount);

       setTimeout(() => {
         this.#restoreProtectedElements(maybeItemCount);
         
         document.dispatchEvent(new CustomEvent('cart:update-complete', { 
           detail: { sectionId: this.sectionId } 
         }));
       }, 50);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      // When third-party scripts dispatch `cart:update` without section HTML, avoid morphing the whole
      // section (e.g. the cart drawer <dialog>) because that can reset open/close state.
      // Instead, fetch the section HTML and morph only this component subtree.
      this.#preserveProtectedElements();

      // Best-effort: if the event carries an itemCount, keep the drawer empty class in sync.
      const maybeItemCount = typeof event?.detail?.data?.itemCount === 'number' ? event.detail.data.itemCount : null;
      if (typeof maybeItemCount === 'number') this.#syncDrawerEmptyState(maybeItemCount);

      sectionRenderer
        .getSectionHTML(this.sectionId, false)
        .then((sectionHTML) => {
          const newSectionDocument = new DOMParser().parseFromString(sectionHTML, 'text/html');
          const newCartItemsComponent =
            this.#findMatchingComponentInSectionHTML(newSectionDocument) ||
            newSectionDocument.querySelector('cart-items-component');

          if (newCartItemsComponent instanceof HTMLElement) {
            morph(this, newCartItemsComponent, { childrenOnly: true });
          } else {
            morphSection(this.sectionId, sectionHTML);
          }

          setTimeout(() => {
            this.#restoreProtectedElements(maybeItemCount);
            document.dispatchEvent(
              new CustomEvent('cart:update-complete', {
                detail: { sectionId: this.sectionId },
              })
            );
          }, 50);

          this.#updateCartQuantitySelectorButtonStates();
        })
        .catch((error) => {
          console.warn('[cart-items-component] Failed to refresh section HTML:', error);
          // Fallback to full section render if our safer path fails.
          sectionRenderer.renderSection(this.sectionId, { cache: false });
        });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(
        `quantity-selector-component[data-variant-id="${variantId}"], cart-quantity-selector-component[data-variant-id="${variantId}"]`
      );

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    const cartQuantitySelectors = document.querySelectorAll('cart-quantity-selector-component');
    for (const selector of cartQuantitySelectors) {
      if ('updateButtonStates' in selector && typeof selector.updateButtonStates === 'function') {
        selector.updateButtonStates();
      }
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * Finds the cart-items-component element corresponding to this instance inside a section HTML document.
   * @param {Document} sectionDocument
   * @returns {HTMLElement | null}
   */
  #findMatchingComponentInSectionHTML(sectionDocument) {
    const sectionWrapper = sectionDocument.getElementById(`shopify-section-${this.sectionId}`) ?? sectionDocument;
    const candidates = sectionWrapper.querySelectorAll('cart-items-component');

    // Prefer an exact match on section id when possible (most reliable when multiple
    // cart-items-component instances exist, e.g. cart page + drawer).
    for (const candidate of candidates) {
      if (candidate instanceof HTMLElement && candidate.dataset.sectionId === this.sectionId) {
        return candidate;
      }
    }

    // Some sections are rendered statically (or with defaults) and might not preserve
    // the same `data-section-id` value in the response. If there's only one candidate,
    // use it to avoid falling back to morphing the entire section (which can disturb
    // open dialogs like the cart drawer).
    if (candidates.length === 1 && candidates[0] instanceof HTMLElement) {
      return candidates[0];
    }

    return null;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
